// ============================================================
// /square/comms — Communications lane.
//
// This is the operator-grade communications surface, NOT a Slack
// client. It reads from sq_comms_events (via /api/comms/events)
// and shows every operationally meaningful event in Square Code:
//
//   • Slack delivery events + health
//   • Operational alerts
//   • Gamma / workspace run terminal events
//   • Site deployment terminal events
//   • Command dispatch failures + denials
//   • OLIVIUM warning / critical events
//   • Cron sync failures
//
// Slack stays the EXTERNAL communication sink. Comms is the
// INTERNAL communication surface. They are reconciled through
// sq_comms_events: each emitter records the row immediately and
// then the Slack provider's outcome is folded in as
// delivery_status.
// ============================================================

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Banner,
  Button,
  EmptyState,
  InlineAlert,
  SeverityBadge,
} from '@square/ui'
import { api, ApiError, isAuthError } from '../../api/client'
import { useCapabilities } from '../../hooks/useCapabilities'
import RouteHeader from '../RouteHeader'

const POLL_INTERVAL_MS = 10_000

type Severity = 'info' | 'warning' | 'critical' | 'success' | 'error'
type DeliveryStatus =
  | 'pending'
  | 'sent'
  | 'failed'
  | 'skipped'
  | 'not_applicable'
  | 'dead_letter'

interface CommsEvent {
  id: string
  project_id: string | null
  workspace_id: string | null
  deployment_id: string | null
  command_id: string | null
  source_system: string
  event_type: string
  severity: Severity
  title: string
  body: string | null
  payload_json: Record<string, unknown>
  external_target: string | null
  external_ref: string | null
  delivery_status: DeliveryStatus
  created_at: string
  updated_at: string
}

interface HealthReport {
  slack: { configured: boolean; webhook: { resolved: boolean; via: string | null } }
  email: { configured: boolean }
  comms_table: { ready: boolean; error: string | null }
  last_slack_delivery_at: string | null
  retries_available: boolean
}

const FILTERS: Array<{ id: string; label: string; sources?: string[] }> = [
  { id: 'all', label: 'All' },
  { id: 'slack', label: 'Slack', sources: ['slack'] },
  { id: 'alerts', label: 'Alerts', sources: ['olivium'] },
  { id: 'deployments', label: 'Deployments', sources: ['deployment'] },
  { id: 'workspace', label: 'Workspace Runs', sources: ['gamma', 'workspace'] },
  { id: 'commands', label: 'Commands', sources: ['command'] },
]

const SEV_FILTERS: Array<{ id: string; label: string; values?: Severity[] }> = [
  { id: 'all', label: 'All severities' },
  { id: 'critical', label: 'Critical', values: ['critical'] },
  { id: 'errors', label: 'Errors', values: ['error', 'critical'] },
  { id: 'warnings', label: 'Warnings', values: ['warning'] },
  { id: 'info', label: 'Info', values: ['info', 'success'] },
]

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime()
  if (isNaN(t)) return iso
  const diff = Math.max(0, Date.now() - t)
  const sec = Math.floor(diff / 1000)
  if (sec < 5) return 'just now'
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  return `${d}d ago`
}

function formatErrorForDisplay(err: unknown): string {
  // Reason this exists: the previous Comms surface rendered the
  // raw thrown value, which produced "[object Object]: request
  // failed" whenever something other than a plain Error was
  // thrown. Always return a readable string here.
  if (!err) return 'unknown error'
  if (err instanceof ApiError) {
    // The api/client humanizer now puts the code into the message
    // when no structured message exists, so they can collide. Don't
    // double-print "missing_token: missing token" — show the code
    // alone in that case.
    if (!err.message || err.message === err.code || err.message === err.code.replace(/_/g, ' ')) {
      return err.code
    }
    return `${err.code}: ${err.message}`
  }
  if (err instanceof Error) {
    return err.message || err.name || 'request failed'
  }
  if (typeof err === 'string') return err
  if (typeof err === 'object') {
    const obj = err as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
    try {
      return JSON.stringify(err)
    } catch {
      return String(err)
    }
  }
  return String(err)
}

function deliveryColor(s: DeliveryStatus): string {
  switch (s) {
    case 'sent':
      return 'var(--olv-success, #4ade80)'
    case 'failed':
    case 'dead_letter':
      return 'var(--olv-danger, #ef4444)'
    case 'pending':
      return 'var(--olv-warning, #facc15)'
    case 'skipped':
      return 'var(--olv-text-tertiary)'
    case 'not_applicable':
    default:
      return 'var(--olv-text-tertiary)'
  }
}

export default function CommsIndex() {
  const { cap } = useCapabilities()
  const emailCap = cap('email_delivery')
  const [events, setEvents] = useState<CommsEvent[]>([])
  const [health, setHealth] = useState<HealthReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [info, setInfo] = useState<string | null>(null)
  const [sourceFilter, setSourceFilter] = useState('all')
  const [severityFilter, setSeverityFilter] = useState('all')
  const [retryingId, setRetryingId] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)

  const fetchEvents = useCallback(async () => {
    try {
      const filter = FILTERS.find((f) => f.id === sourceFilter)
      const sevFilter = SEV_FILTERS.find((f) => f.id === severityFilter)
      const r = await api.comms.events({
        source: filter?.sources?.join(','),
        severity: sevFilter?.values?.join(','),
        limit: 100,
      })
      setEvents(r.events as CommsEvent[])
      setError(null)
      setAuthRequired(false)
    } catch (err) {
      // Auth-required is the expected pre-sign-in state, not a
      // system fault. Don't surface it as a critical error banner.
      if (isAuthError(err)) {
        setAuthRequired(true)
        setError(null)
        setEvents([])
      } else {
        setError(formatErrorForDisplay(err))
      }
    }
  }, [sourceFilter, severityFilter])

  const fetchHealth = useCallback(async () => {
    try {
      const r = await api.comms.health()
      setHealth(r as unknown as HealthReport)
    } catch (err) {
      // Health failure should not blank the surface — just stash
      // the error in the readiness strip implicitly. Auth errors
      // are also silent here; the events fetch handles the visible
      // auth-required state.
      setHealth(null)
      if (!isAuthError(err)) {
        // eslint-disable-next-line no-console
        console.warn('[comms] health probe failed:', formatErrorForDisplay(err))
      }
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([fetchEvents(), fetchHealth()]).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [fetchEvents, fetchHealth])

  useEffect(() => {
    const t = setInterval(() => {
      void fetchEvents()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(t)
  }, [fetchEvents])

  async function onRetry(id: string) {
    setRetryingId(id)
    try {
      const r = await api.comms.retry(id)
      setInfo(r.note ?? `Re-queue result: ${r.status}${r.noop ? ' (no change needed)' : ''}`)
      void fetchEvents()
    } catch (err) {
      setError(formatErrorForDisplay(err))
    } finally {
      setRetryingId(null)
    }
  }

  async function onSendTest(target: 'slack' | 'email') {
    setTesting(true)
    try {
      const r = await api.comms.test(target)
      setInfo(
        `${target} test sent — delivery: ${r.delivery_status ?? 'unknown'}${
          r.note ? ` (${r.note})` : ''
        }`,
      )
      void fetchEvents()
    } catch (err) {
      setError(formatErrorForDisplay(err))
    } finally {
      setTesting(false)
    }
  }

  const filtered = events
  const counts = useMemo(() => {
    const c = { critical: 0, error: 0, warning: 0, success: 0, info: 0 }
    for (const ev of events) c[ev.severity]++
    return c
  }, [events])

  return (
    <div>
      <RouteHeader
        kicker="SQUARE CODE"
        title="Comms Ops"
        description="Operator communication monitor — Slack delivery, deployment events, workspace runs, command dispatch, alerts, and system notifications."
        crumbs={[{ label: 'Communications' }]}
      />

      {/* Readiness strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <ReadinessTile
          label="Slack"
          ok={!!health?.slack?.configured}
          detail={
            health?.slack?.webhook?.via
              ? `via ${health.slack.webhook.via}`
              : 'webhook not resolved'
          }
        />
        <ReadinessTile
          label="Email"
          ok={!!health?.email?.configured}
          detail={health?.email?.configured ? 'Resend configured' : 'Resend not set'}
        />
        <ReadinessTile
          label="Comms table"
          ok={!!health?.comms_table?.ready}
          detail={health?.comms_table?.ready ? 'sq_comms_events ready' : health?.comms_table?.error || 'not ready'}
        />
        <ReadinessTile
          label="Last Slack delivery"
          ok={!!health?.last_slack_delivery_at}
          detail={
            health?.last_slack_delivery_at
              ? relativeTime(health.last_slack_delivery_at)
              : 'no successful delivery yet'
          }
        />
        <ReadinessTile
          label="Retries"
          ok={!!health?.retries_available}
          detail={health?.retries_available ? 'available' : 'unavailable'}
        />
      </div>

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="critical" title="Comms error" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        </div>
      )}
      {info && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="info" title="Comms" onDismiss={() => setInfo(null)}>
            {info}
          </Banner>
        </div>
      )}

      {/* Operator actions */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 14,
          alignItems: 'center',
        }}
      >
        <Button
          variant="secondary"
          loading={testing}
          onClick={() => onSendTest('slack')}
          disabled={!health?.slack?.configured}
        >
          Send Slack test
        </Button>
        <Button
          variant="secondary"
          loading={testing}
          onClick={() => onSendTest('email')}
          disabled={emailCap?.state === 'blocked' || emailCap?.state === 'unavailable'}
          title={emailCap && emailCap.state !== 'live' ? emailCap.reason : undefined}
        >
          Send email test
          {emailCap && emailCap.state !== 'live' ? ` (${emailCap.state})` : ''}
        </Button>
        <Button variant="ghost" onClick={() => void fetchEvents()}>
          Refresh
        </Button>
        {/* Deliberately NOT a live region — the counter changes on every
            10s poll and would spam screen readers; errors already announce
            through Banner/InlineAlert roles. */}
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--olv-text-tertiary)',
          }}
        >
          {events.length} events · crit {counts.critical} · err {counts.error} · warn {counts.warning}
        </span>
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <FilterChip
            key={f.id}
            label={f.label}
            active={sourceFilter === f.id}
            onClick={() => setSourceFilter(f.id)}
          />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {SEV_FILTERS.map((f) => (
          <FilterChip
            key={f.id}
            label={f.label}
            active={severityFilter === f.id}
            onClick={() => setSeverityFilter(f.id)}
            tone="severity"
          />
        ))}
      </div>

      {loading ? (
        <InlineAlert severity="info">Loading communications…</InlineAlert>
      ) : authRequired ? (
        <EmptyState message="Sign in to view operator communications. /api/comms/events requires an authenticated session — sign in from /auth/login to load the event log." />
      ) : filtered.length === 0 ? (
        <EmptyState message="No communication events yet. Trigger a deployment, run a workspace, or send a Slack test to see events here." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((ev) => (
            <EventCard
              key={ev.id}
              ev={ev}
              onRetry={onRetry}
              retrying={retryingId === ev.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ReadinessTile({
  label,
  ok,
  detail,
}: {
  label: string
  ok: boolean
  detail: string
}) {
  return (
    <div
      style={{
        background: 'var(--olv-panel-bg)',
        border: '1px solid var(--olv-panel-border)',
        borderRadius: 'var(--olv-radius-card)',
        backdropFilter: 'var(--olv-panel-blur)',
        padding: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: 'var(--olv-text-tertiary)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: ok ? 'var(--olv-success, #4ade80)' : 'var(--olv-warning, #facc15)',
            boxShadow: ok ? '0 0 8px rgba(74,222,128,0.4)' : 'none',
          }}
        />
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 11,
          color: 'var(--olv-text-secondary)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {detail}
      </div>
    </div>
  )
}

function FilterChip({
  label,
  active,
  onClick,
  tone,
}: {
  label: string
  active: boolean
  onClick: () => void
  tone?: 'severity'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid',
        borderColor: active
          ? 'rgba(123,94,167,0.30)'
          : 'var(--olv-panel-border)',
        background: active ? 'rgba(123,94,167,0.12)' : 'transparent',
        color: active ? 'var(--olv-purple)' : 'var(--olv-text-secondary)',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        transition: 'all 200ms var(--ease-standard)',
        opacity: tone === 'severity' && !active ? 0.85 : 1,
      }}
    >
      {label}
    </button>
  )
}

function EventCard({
  ev,
  onRetry,
  retrying,
}: {
  ev: CommsEvent
  onRetry: (id: string) => void
  retrying: boolean
}) {
  // Retry = re-queue for the claim-locked email worker. Slack is
  // retired — its rows keep their historical status but offer no
  // action (the server 410s them).
  const canRetry =
    ev.external_target === 'email' &&
    (ev.delivery_status === 'failed' || ev.delivery_status === 'dead_letter')
  return (
    <article
      style={{
        background: 'var(--olv-panel-bg)',
        border: '1px solid var(--olv-panel-border)',
        borderRadius: 'var(--olv-radius-card)',
        backdropFilter: 'var(--olv-panel-blur)',
        padding: 14,
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 6,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <SeverityBadge outcome={ev.severity as any} />
          <span
            style={{
              fontSize: 13,
              color: 'var(--olv-text-primary)',
              fontWeight: 600,
            }}
          >
            {ev.title}
          </span>
        </div>
        <span
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--olv-text-tertiary)',
            whiteSpace: 'nowrap',
          }}
        >
          {relativeTime(ev.created_at)}
        </span>
      </header>

      {ev.body && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--olv-text-secondary)',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            marginBottom: 8,
          }}
        >
          {ev.body}
        </div>
      )}

      <footer
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          color: 'var(--olv-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        <span>source: {ev.source_system}</span>
        <span>type: {ev.event_type}</span>
        {ev.project_id && <span>project: {ev.project_id.slice(0, 8)}</span>}
        {ev.workspace_id && <span>workspace: {ev.workspace_id.slice(0, 8)}</span>}
        {ev.deployment_id && <span>deploy: {ev.deployment_id.slice(0, 8)}</span>}
        {ev.command_id && <span>cmd: {ev.command_id.slice(0, 8)}</span>}
        {ev.external_target === 'slack' && (
          <span
            style={{ color: 'var(--olv-text-tertiary)' }}
            title="Slack is retired — historical status only; no delivery path exists."
          >
            slack (retired): {ev.delivery_status}
          </span>
        )}
        {ev.external_target === 'email' && (
          <span style={{ color: deliveryColor(ev.delivery_status) }}>
            email: {ev.delivery_status}
          </span>
        )}
        {canRetry && (
          <button
            type="button"
            onClick={() => onRetry(ev.id)}
            disabled={retrying}
            title="Re-queues the event for the claim-locked email worker — no direct send from the browser."
            style={{
              marginLeft: 'auto',
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid var(--olv-panel-border)',
              background: 'rgba(123,94,167,0.12)',
              color: 'var(--olv-purple)',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              cursor: retrying ? 'wait' : 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {retrying ? 're-queueing…' : 're-queue'}
          </button>
        )}
      </footer>
    </article>
  )
}
